import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Container, Typography, Paper, Button, Stack, Box, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert,
  Select, MenuItem, TextField, FormControl, InputLabel, Autocomplete,
  Grid, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import {
  Add, Edit, Delete, CheckCircle, PictureAsPdf, TableChart, Tune, Close, Category
} from '@mui/icons-material';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL;

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('pt-BR');
};

const FORM_VAZIO = {
  fornecedor_id: null,
  valor: '',
  categoria_id: null,
  descricao: '',
  data_vencimento: '',
  data_pagamento: '',
  status: 'Pendente',
  forma_pagamento: 'Dinheiro',
  observacao: ''
};

const formasPagamento = [
  'PIX', 'Dinheiro', 'Cartão de Crédito', 'Cartão de Débito',
  'Transferência Bancária (TED)', 'Transferência Bancária (DOC)',
  'Transferência', 'Cheque', 'Saldo em Conta'
];

const inputStyle = {
  '& .MuiOutlinedInput-root': {
    '&:hover fieldset': { borderColor: '#FF8C00' },
    '&.Mui-focused fieldset': { borderColor: '#FF8C00', borderWidth: 2 }
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#FF8C00' }
};

export default function ContasPagar() {
  const [contas, setContas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);

  // Nova Conta / Editar
  const [openDialog, setOpenDialog] = useState(false);
  const [editando, setEditando] = useState(null);
  const [novaConta, setNovaConta] = useState(FORM_VAZIO);

  // Alterar Status
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [contaSelecionada, setContaSelecionada] = useState(null);

  // Gerenciar Atributos
  const [atributosOpen, setAtributosOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [catEditando, setCatEditando] = useState(null);
  const [catForm, setCatForm] = useState({ nome: '', descricao: '' });

  // Quick create categoria
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickForm, setQuickForm] = useState({ nome: '', descricao: '' });

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const token = () => localStorage.getItem('token');
  const authHeader = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  useEffect(() => {
    carregarContas();
    carregarCategorias();
    carregarFornecedores();
  }, []);

  // ========== CARREGAMENTOS ==========
  const carregarContas = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/contas-pagar`, authHeader());
      setContas(data || []);
    } catch {
      setSnackbar({ open: true, message: 'Erro ao carregar contas a pagar', severity: 'error' });
    }
  };

  const carregarCategorias = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/categorias-pagamento`, authHeader());
      setCategorias(data || []);
    } catch {
      setCategorias([]);
    }
  };

  const carregarFornecedores = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/fornecedores`, authHeader());
      setFornecedores(data || []);
    } catch {
      setFornecedores([]);
    }
  };

  // ========== NOVA CONTA ==========
  const handleOpenDialog = (conta = null) => {
    if (conta) {
      setNovaConta({
        fornecedor_id: conta.fornecedor_id || null,
        valor: conta.valor || '',
        categoria_id: conta.categoria_id || null,
        descricao: conta.descricao || '',
        data_vencimento: conta.data_vencimento?.split('T')[0] || '',
        data_pagamento: conta.data_pagamento?.split('T')[0] || '',
        status: conta.status || 'Pendente',
        forma_pagamento: conta.forma_pagamento || 'Dinheiro',
        observacao: conta.observacao || ''
      });
      setEditando(conta.id);
    } else {
      setNovaConta(FORM_VAZIO);
      setEditando(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditando(null);
    setNovaConta(FORM_VAZIO);
  };

  const handleChange = (e) => setNovaConta({ ...novaConta, [e.target.name]: e.target.value });

  const handleSalvar = async () => {
    if (!novaConta.valor || !novaConta.descricao || !novaConta.data_vencimento) {
      setSnackbar({ open: true, message: 'Preencha os campos obrigatórios: descrição, valor e data de vencimento', severity: 'warning' });
      return;
    }
    try {
      const dados = { ...novaConta, valor: parseFloat(novaConta.valor) };
      if (editando) {
        await axios.put(`${API_URL}/contas-pagar/${editando}`, dados, authHeader());
        setSnackbar({ open: true, message: 'Conta atualizada com sucesso!', severity: 'success' });
      } else {
        await axios.post(`${API_URL}/contas-pagar`, dados, authHeader());
        setSnackbar({ open: true, message: 'Conta cadastrada com sucesso!', severity: 'success' });
      }
      carregarContas();
      handleCloseDialog();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.mensagem || 'Erro ao salvar conta', severity: 'error' });
    }
  };

  const handleExcluir = async (id) => {
    if (!window.confirm('Deseja realmente excluir esta conta?')) return;
    try {
      await axios.delete(`${API_URL}/contas-pagar/${id}`, authHeader());
      setSnackbar({ open: true, message: 'Conta excluída com sucesso!', severity: 'success' });
      carregarContas();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.mensagem || 'Erro ao excluir conta', severity: 'error' });
    }
  };

  // ========== ALTERAR STATUS ==========
  const handleOpenStatusDialog = (conta) => {
    setContaSelecionada({
      ...conta,
      data_pagamento: conta.data_pagamento?.split('T')[0] || new Date().toISOString().split('T')[0],
      forma_pagamento: conta.forma_pagamento || 'Dinheiro'
    });
    setOpenStatusDialog(true);
  };

  const handleAtualizarStatus = async () => {
    try {
      await axios.patch(`${API_URL}/contas-pagar/${contaSelecionada.id}/status`, {
        status: contaSelecionada.status,
        data_pagamento: contaSelecionada.data_pagamento,
        forma_pagamento: contaSelecionada.forma_pagamento
      }, authHeader());
      setSnackbar({ open: true, message: 'Status atualizado com sucesso!', severity: 'success' });
      carregarContas();
      setOpenStatusDialog(false);
      setContaSelecionada(null);
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.mensagem || 'Erro ao atualizar status', severity: 'error' });
    }
  };

  // ========== GERENCIAR ATRIBUTOS — CATEGORIAS ==========
  const abrirCatDialog = (cat = null) => {
    if (cat) {
      setCatForm({ nome: cat.nome || '', descricao: cat.descricao || '' });
      setCatEditando(cat.id);
    } else {
      setCatForm({ nome: '', descricao: '' });
      setCatEditando(null);
    }
    setCatOpen(true);
  };

  const fecharCatDialog = () => {
    setCatOpen(false);
    setCatEditando(null);
    setCatForm({ nome: '', descricao: '' });
  };

  const salvarCategoria = async () => {
    if (!catForm.nome.trim()) {
      setSnackbar({ open: true, message: 'Nome é obrigatório', severity: 'warning' });
      return;
    }
    try {
      if (catEditando) {
        await axios.put(`${API_URL}/categorias-pagamento/${catEditando}`, catForm, authHeader());
        setSnackbar({ open: true, message: 'Categoria atualizada!', severity: 'success' });
      } else {
        await axios.post(`${API_URL}/categorias-pagamento`, catForm, authHeader());
        setSnackbar({ open: true, message: 'Categoria criada!', severity: 'success' });
      }
      fecharCatDialog();
      carregarCategorias();
    } catch {
      setSnackbar({ open: true, message: 'Erro ao salvar categoria', severity: 'error' });
    }
  };

  const excluirCategoria = async (id) => {
    if (!window.confirm('Excluir esta categoria?')) return;
    try {
      await axios.delete(`${API_URL}/categorias-pagamento/${id}`, authHeader());
      setSnackbar({ open: true, message: 'Categoria excluída!', severity: 'success' });
      carregarCategorias();
    } catch {
      setSnackbar({ open: true, message: 'Erro ao excluir categoria', severity: 'error' });
    }
  };

  // ========== QUICK CREATE CATEGORIA ==========
  const salvarQuick = async () => {
    if (!quickForm.nome.trim()) {
      setSnackbar({ open: true, message: 'Nome é obrigatório', severity: 'warning' });
      return;
    }
    try {
      const { data } = await axios.post(`${API_URL}/categorias-pagamento`, quickForm, authHeader());
      await carregarCategorias();
      setNovaConta(f => ({ ...f, categoria_id: data.id || f.categoria_id }));
      setSnackbar({ open: true, message: 'Categoria criada!', severity: 'success' });
      setQuickOpen(false);
    } catch {
      setSnackbar({ open: true, message: 'Erro ao criar categoria', severity: 'error' });
    }
  };

  // ========== EXPORTAÇÕES ==========
  const exportarPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFillColor(31, 31, 31);
    doc.rect(0, 0, pageWidth, 25, 'F');
    doc.setTextColor(247, 139, 0);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('EASY LOCADORA', pageWidth / 2, 12, { align: 'center' });
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.text('CONTAS A PAGAR', pageWidth / 2, 20, { align: 'center' });

    const totalPendente = contas.filter(c => c.status === 'Pendente').reduce((a, c) => a + Number(c.valor), 0);
    const totalPago = contas.filter(c => c.status === 'Pago').reduce((a, c) => a + Number(c.valor), 0);

    autoTable(doc, {
      startY: 40,
      head: [['ID', 'Vencimento', 'Descrição', 'Categoria', 'Fornecedor', 'Forma Pgto', 'Valor', 'Status']],
      body: contas.map(c => [
        c.id, formatDate(c.data_vencimento), c.descricao || '-',
        c.categoria_nome || '-', c.fornecedor_nome || '-',
        c.forma_pagamento || 'Dinheiro', formatCurrency(c.valor), c.status
      ]),
      theme: 'grid',
      headStyles: { fillColor: [31, 31, 31], textColor: [247, 139, 0], fontStyle: 'bold' },
      styles: { fontSize: 8, cellPadding: 2 }
    });

    doc.save(`contas-pagar-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportarExcel = () => {
    const dados = [
      ['EASY LOCADORA - CONTAS A PAGAR'],
      [`Gerado em: ${new Date().toLocaleString('pt-BR')}`],
      [],
      ['ID', 'Vencimento', 'Descrição', 'Categoria', 'Fornecedor', 'Forma Pgto', 'Valor', 'Status'],
      ...contas.map(c => [
        c.id, formatDate(c.data_vencimento), c.descricao || '-',
        c.categoria_nome || '-', c.fornecedor_nome || '-',
        c.forma_pagamento || 'Dinheiro', c.valor, c.status
      ])
    ];
    const ws = XLSX.utils.aoa_to_sheet(dados);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Contas a Pagar');
    XLSX.writeFile(wb, `contas-pagar-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // ========== COLUNAS ==========
  const colunas = useMemo(() => [
    { accessorKey: 'id', header: 'ID', size: 60 },
    { accessorKey: 'data_vencimento', header: 'Vencimento', size: 110, Cell: ({ cell }) => formatDate(cell.getValue()) },
    { accessorKey: 'descricao', header: 'Descrição', size: 220 },
    { accessorKey: 'categoria_nome', header: 'Categoria', size: 140, Cell: ({ cell }) => cell.getValue() || '-' },
    { accessorKey: 'fornecedor_nome', header: 'Fornecedor', size: 160, Cell: ({ cell }) => cell.getValue() || '-' },
    { accessorKey: 'valor', header: 'Valor', size: 110, Cell: ({ cell }) => formatCurrency(cell.getValue()) },
    {
      accessorKey: 'status', header: 'Status', size: 110,
      Cell: ({ cell }) => {
        const s = cell.getValue();
        const color = s === 'Pago' ? 'success' : s === 'Pendente' ? 'warning' : 'error';
        return <Chip label={s} color={color} size="small" />;
      }
    },
    {
      accessorKey: 'acoes', header: 'Ações', size: 120,
      Cell: ({ row }) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Alterar Status">
            <IconButton size="small" color="success" onClick={() => handleOpenStatusDialog(row.original)}>
              <CheckCircle fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton size="small" color="primary" onClick={() => handleOpenDialog(row.original)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir">
            <IconButton size="small" color="error" onClick={() => handleExcluir(row.original.id)}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    }
  ], []);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* HEADER */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3,
        background: 'linear-gradient(135deg, #1f1f1f 0%, #3a3a3a 100%)',
        p: 3, borderRadius: 2, color: '#fff'
      }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#f78b00' }}>
            💳 Contas a Pagar
          </Typography>
          <Typography variant="body2" sx={{ color: '#aaa', mt: 0.5 }}>
            Gerencie os pagamentos e despesas
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" startIcon={<PictureAsPdf />} onClick={exportarPDF} disabled={contas.length === 0}
            sx={{ borderColor: '#aaa', color: '#aaa', '&:hover': { borderColor: '#fff', color: '#fff' } }}>
            PDF
          </Button>
          <Button variant="outlined" startIcon={<TableChart />} onClick={exportarExcel} disabled={contas.length === 0}
            sx={{ borderColor: '#aaa', color: '#aaa', '&:hover': { borderColor: '#fff', color: '#fff' } }}>
            Excel
          </Button>
          <Button variant="outlined" startIcon={<Tune />} onClick={() => setAtributosOpen(true)}
            sx={{ borderColor: '#f78b00', color: '#f78b00', fontWeight: 600, '&:hover': { bgcolor: 'rgba(247,139,0,0.1)' } }}>
            Gerenciar Atributos
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}
            sx={{ bgcolor: '#f78b00', fontWeight: 700, '&:hover': { bgcolor: '#e67e00' } }}>
            Nova Conta
          </Button>
        </Stack>
      </Box>

      {/* TABELA */}
      <Paper sx={{ p: 2 }}>
        <MaterialReactTable
          columns={colunas} data={contas}
          localization={MRT_Localization_PT_BR}
          enablePagination enableSorting enableColumnFilters enableDensityToggle
          initialState={{ density: 'compact', pagination: { pageSize: 25 } }}
          muiTableProps={{ sx: { tableLayout: 'fixed' } }}
        />
      </Paper>

      {/* ========== MODAL: NOVA CONTA / EDITAR ========== */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #1f1f1f 0%, #3a3a3a 100%)',
          color: '#f78b00', fontWeight: 700,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2.5
        }}>
          💳 {editando ? 'Editar Conta a Pagar' : 'Nova Conta a Pagar'}
          <IconButton onClick={handleCloseDialog} sx={{ color: '#fff' }} size="small"><Close /></IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2.5} sx={{ mt: 0 }}>

            {/* IDENTIFICAÇÃO */}
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ color: '#f78b00', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                Identificação
              </Typography>
              <Divider sx={{ mt: 0.5, mb: 1.5, borderColor: '#f78b00', opacity: 0.3 }} />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="descricao" label="Descrição *" value={novaConta.descricao}
                onChange={handleChange} fullWidth
                placeholder="Ex: Aluguel do ponto, conta de energia, fornecedor..."
                sx={inputStyle}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={fornecedores}
                value={fornecedores.find(f => f.id === novaConta.fornecedor_id) || null}
                onChange={(_, val) => setNovaConta(f => ({ ...f, fornecedor_id: val ? val.id : null }))}
                getOptionLabel={opt => opt?.nome || ''}
                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                renderInput={params => (
                  <TextField {...params} label="Fornecedor"
                    helperText={`${fornecedores.length} fornecedor(es) disponível(is)`}
                    sx={inputStyle}
                  />
                )}
                noOptionsText="Nenhum fornecedor cadastrado"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="valor" label="Valor (R$) *" type="number" value={novaConta.valor}
                onChange={handleChange} fullWidth
                inputProps={{ step: '0.01', min: '0' }} sx={inputStyle}
              />
            </Grid>

            {/* CLASSIFICAÇÃO */}
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ color: '#f78b00', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                Classificação
              </Typography>
              <Divider sx={{ mt: 0.5, mb: 1.5, borderColor: '#f78b00', opacity: 0.3 }} />
            </Grid>

            <Grid item xs={12} sm={8}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <FormControl fullWidth sx={inputStyle}>
                  <InputLabel>Categoria</InputLabel>
                  <Select name="categoria_id" value={novaConta.categoria_id || ''} onChange={handleChange} label="Categoria">
                    <MenuItem value=""><em>Nenhuma</em></MenuItem>
                    {categorias.map(cat => <MenuItem key={cat.id} value={cat.id}>{cat.nome}</MenuItem>)}
                  </Select>
                  <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, ml: 1.5 }}>
                    {`${API_URL}/categorias-pagamento — ${categorias.length} cadastrada(s)`}
                  </Typography>
                </FormControl>
                <Tooltip title="Criar nova categoria">
                  <IconButton onClick={() => { setQuickForm({ nome: '', descricao: '' }); setQuickOpen(true); }}
                    sx={{ mt: 1, color: '#f78b00', border: '1px solid #f78b00', borderRadius: 1, '&:hover': { bgcolor: 'rgba(247,139,0,0.1)' } }}
                    size="small">
                    <Add fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Grid>

            {/* DATAS E PAGAMENTO */}
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ color: '#f78b00', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                Datas e Pagamento
              </Typography>
              <Divider sx={{ mt: 0.5, mb: 1.5, borderColor: '#f78b00', opacity: 0.3 }} />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField name="data_vencimento" label="Data de Vencimento *" type="date"
                value={novaConta.data_vencimento} onChange={handleChange}
                fullWidth InputLabelProps={{ shrink: true }} sx={inputStyle}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField name="data_pagamento" label="Data de Pagamento" type="date"
                value={novaConta.data_pagamento} onChange={handleChange}
                fullWidth InputLabelProps={{ shrink: true }} sx={inputStyle}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <FormControl fullWidth sx={inputStyle}>
                <InputLabel>Forma de Pagamento</InputLabel>
                <Select name="forma_pagamento" value={novaConta.forma_pagamento} onChange={handleChange} label="Forma de Pagamento">
                  {formasPagamento.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3}>
              <FormControl fullWidth sx={inputStyle}>
                <InputLabel>Status</InputLabel>
                <Select name="status" value={novaConta.status} onChange={handleChange} label="Status">
                  <MenuItem value="Pendente">Pendente</MenuItem>
                  <MenuItem value="Pago">Pago</MenuItem>
                  <MenuItem value="Atrasado">Atrasado</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* OBSERVAÇÃO */}
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ color: '#f78b00', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                Observação
              </Typography>
              <Divider sx={{ mt: 0.5, mb: 1.5, borderColor: '#f78b00', opacity: 0.3 }} />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="observacao" label="Observação" value={novaConta.observacao}
                onChange={handleChange} fullWidth multiline rows={3}
                placeholder="Informações adicionais, número do boleto, referência de contrato..."
                sx={inputStyle}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #eee', gap: 1 }}>
          <Button onClick={handleCloseDialog} variant="outlined" sx={{ borderColor: '#ccc', color: '#666' }}>Cancelar</Button>
          <Button onClick={handleSalvar} variant="contained"
            sx={{ bgcolor: '#f78b00', fontWeight: 700, px: 4, '&:hover': { bgcolor: '#e67e00' } }}>
            {editando ? 'Atualizar' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========== MODAL: ALTERAR STATUS ========== */}
      <Dialog open={openStatusDialog} onClose={() => setOpenStatusDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #1f1f1f 0%, #3a3a3a 100%)',
          color: '#f78b00', fontWeight: 700,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2.5
        }}>
          Alterar Status do Pagamento
          <IconButton onClick={() => setOpenStatusDialog(false)} sx={{ color: '#fff' }} size="small"><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 1.5, p: 2, mb: 3, border: '1px solid #e0e0e0' }}>
            <Typography variant="body2" color="text.secondary">Descrição</Typography>
            <Typography variant="subtitle1" fontWeight={600}>{contaSelecionada?.descricao}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Valor</Typography>
            <Typography variant="h6" fontWeight={700} sx={{ color: '#e53935' }}>
              {formatCurrency(contaSelecionada?.valor)}
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth sx={inputStyle}>
                <InputLabel>Novo Status</InputLabel>
                <Select value={contaSelecionada?.status || 'Pendente'} label="Novo Status"
                  onChange={(e) => setContaSelecionada({ ...contaSelecionada, status: e.target.value })}>
                  <MenuItem value="Pendente">Pendente</MenuItem>
                  <MenuItem value="Pago">Pago</MenuItem>
                  <MenuItem value="Atrasado">Atrasado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {contaSelecionada?.status === 'Pago' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField label="Data de Pagamento" type="date"
                    value={contaSelecionada?.data_pagamento || ''}
                    onChange={(e) => setContaSelecionada({ ...contaSelecionada, data_pagamento: e.target.value })}
                    fullWidth InputLabelProps={{ shrink: true }} sx={inputStyle}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth sx={inputStyle}>
                    <InputLabel>Forma de Pagamento</InputLabel>
                    <Select value={contaSelecionada?.forma_pagamento || 'Dinheiro'} label="Forma de Pagamento"
                      onChange={(e) => setContaSelecionada({ ...contaSelecionada, forma_pagamento: e.target.value })}>
                      {formasPagamento.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #eee', gap: 1 }}>
          <Button onClick={() => setOpenStatusDialog(false)} variant="outlined" sx={{ borderColor: '#ccc', color: '#666' }}>Cancelar</Button>
          <Button onClick={handleAtualizarStatus} variant="contained" color="success" sx={{ fontWeight: 700, px: 4 }}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========== MODAL: GERENCIAR ATRIBUTOS ========== */}
      <Dialog open={atributosOpen} onClose={() => setAtributosOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden', minHeight: 480 } }}>
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #1f1f1f 0%, #3a3a3a 100%)',
          color: '#f78b00', fontWeight: 700,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2.5
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tune sx={{ color: '#f78b00' }} /> Gerenciar Atributos Financeiros
          </Box>
          <IconButton onClick={() => setAtributosOpen(false)} sx={{ color: '#fff' }} size="small"><Close /></IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                <Category fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle', color: '#f78b00' }} />
                Categorias de Pagamento
              </Typography>
              <Typography variant="caption" color="text.secondary">Endpoint: {API_URL}/categorias-pagamento</Typography>
            </Box>
            <Button variant="contained" startIcon={<Add />} size="small" onClick={() => abrirCatDialog()}
              sx={{ bgcolor: '#f78b00', '&:hover': { bgcolor: '#e67e00' } }}>
              Nova Categoria
            </Button>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Nome</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Descrição</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categorias.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ color: '#999', py: 3 }}>Nenhuma categoria cadastrada</TableCell>
                  </TableRow>
                ) : categorias.map(cat => (
                  <TableRow key={cat.id} hover>
                    <TableCell>{cat.id}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{cat.nome}</TableCell>
                    <TableCell sx={{ color: '#666' }}>{cat.descricao || '-'}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" color="primary" onClick={() => abrirCatDialog(cat)}><Edit fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton size="small" color="error" onClick={() => excluirCategoria(cat.id)}><Delete fontSize="small" /></IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>

      {/* ========== DIALOG: EDITAR/CRIAR CATEGORIA ========== */}
      <Dialog open={catOpen} onClose={fecharCatDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{catEditando ? 'Editar Categoria' : 'Nova Categoria de Pagamento'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1.5 }}>
            <TextField label="Nome *" value={catForm.nome} onChange={e => setCatForm(f => ({ ...f, nome: e.target.value }))}
              fullWidth autoFocus placeholder="Ex: Aluguel, Energia, Fornecedor, Manutenção..." sx={inputStyle} />
            <TextField label="Descrição" value={catForm.descricao} onChange={e => setCatForm(f => ({ ...f, descricao: e.target.value }))}
              fullWidth multiline rows={3} placeholder="Descrição detalhada desta categoria..." sx={inputStyle} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={fecharCatDialog} variant="outlined" sx={{ borderColor: '#ccc', color: '#666' }}>Cancelar</Button>
          <Button onClick={salvarCategoria} variant="contained" sx={{ bgcolor: '#f78b00', '&:hover': { bgcolor: '#e67e00' } }}>
            {catEditando ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========== DIALOG: QUICK CREATE CATEGORIA ========== */}
      <Dialog open={quickOpen} onClose={() => setQuickOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Nova Categoria
          <IconButton onClick={() => setQuickOpen(false)} size="small"><Close fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Nome *" value={quickForm.nome} onChange={e => setQuickForm(f => ({ ...f, nome: e.target.value }))}
              fullWidth autoFocus placeholder="Ex: Aluguel, Taxa..." sx={inputStyle} />
            <TextField label="Descrição" value={quickForm.descricao} onChange={e => setQuickForm(f => ({ ...f, descricao: e.target.value }))}
              fullWidth placeholder="Opcional" sx={inputStyle} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setQuickOpen(false)} variant="outlined" sx={{ borderColor: '#ccc', color: '#666' }}>Cancelar</Button>
          <Button onClick={salvarQuick} variant="contained" sx={{ bgcolor: '#f78b00', '&:hover': { bgcolor: '#e67e00' } }}>
            Criar e Selecionar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  );
}
